import type { LifecycleState } from '../services/api';
import { pluralPl } from '../utils/plural';

// Etapy w kolejności workflow (spec §1) + etykiety/kolory (DS tokeny). Wspólne dla
// PipelineView (chipy filtrów) i DashboardView (kafle #10) — jedna prawda o nazwach i kolorach.
//
// Właściciel 2026-09-13 (pkt 22): „Zaplanowany" i „W terenie" to dla operatora JEDEN etap —
// auto-release (2026-08-03) robi z każdego przeglądu z terminem IN_PROGRESS = W_TERENIE, więc
// rozróżnienie nic nie mówiło. Stany w bazie zostają (resolver 037 nietknięty); scalamy w UI:
// jeden chip/kafel „Zaplanowany" (licznik = suma obu stanów, filtr backendu zwraca oba),
// a wiersz W_TERENIE dostaje etykietę „Zaplanowany".
//
// Właściciel 2026-10-02 (docx): „po wpisaniu Nieobecności w lokalu uważamy dokument za gotowy do
// wysłania do CEEB, a on ucieka do innej listy (do powrotu) — niech zostanie razem z innymi".
// Tak samo jak wyżej: stan DO_POWROTU w bazie zostaje (resolver nietknięty), w UI to jeden etap
// „Gotowy" (chip/kafel = suma GOTOWY_DO_CEEB + DO_POWROTU, filtr backendu zwraca oba —
// MERGED_STAGES w routes/pipelineQuery.js). Wiersz DO_POWROTU dostaje pomarańczową odznakę
// „Gotowy · N nieobecnych" (stageBadgeLabel), więc nadal widać, że trzeba wrócić.
export const STATE_ORDER: LifecycleState[] = [
  'NOWY', 'ZAPLANOWANY', 'GOTOWY_DO_CEEB', 'W_CEEB', 'ZAMKNIETY',
];

/** Stany bazy składające się na jeden etap UI (klucz = etap z STATE_ORDER). */
export const STAGE_STATES: Partial<Record<LifecycleState, LifecycleState[]>> = {
  ZAPLANOWANY: ['ZAPLANOWANY', 'W_TERENIE'],
  GOTOWY_DO_CEEB: ['GOTOWY_DO_CEEB', 'DO_POWROTU'],
};

/** Etap UI, do którego należy stan bazy (W_TERENIE → ZAPLANOWANY, DO_POWROTU → GOTOWY_DO_CEEB). */
export function stageOf(state: LifecycleState): LifecycleState {
  const hit = (Object.keys(STAGE_STATES) as LifecycleState[]).find((k) => STAGE_STATES[k]!.includes(state));
  return hit ?? state;
}

/** Licznik etapu UI = suma liczników stanów bazy, które się na niego składają. */
export function stageCount(counts: Partial<Record<LifecycleState, number>> | null | undefined, stage: LifecycleState): number {
  if (!counts) return 0;
  return (STAGE_STATES[stage] || [stage]).reduce((sum, s) => sum + (counts[s] || 0), 0);
}

export interface LifecycleMeta { label: string; fg: string; bg: string; }

export const LIFECYCLE_META: Record<LifecycleState, LifecycleMeta> = {
  NOWY:           { label: 'Nowy',        fg: 'var(--text-secondary)', bg: 'var(--surface-fill)' },
  ZAPLANOWANY:    { label: 'Zaplanowany', fg: 'var(--changed-text)',   bg: 'var(--changed-bg)' },
  W_TERENIE:      { label: 'Zaplanowany', fg: 'var(--changed-text)',   bg: 'var(--changed-bg)' }, // pkt 22: scalony z ZAPLANOWANY
  DO_POWROTU:     { label: 'Gotowy',      fg: 'var(--status-rec)',     bg: 'var(--status-rec-bg)' }, // 2026-10-02: scalony z GOTOWY_DO_CEEB, pomarańczowy = są nieobecni
  GOTOWY_DO_CEEB: { label: 'Gotowy',      fg: 'var(--status-ok)',      bg: 'var(--status-ok-bg)' },
  W_CEEB:         { label: 'W CEEB',      fg: 'var(--status-ok)',      bg: 'var(--status-ok-bg)' },
  ZAMKNIETY:      { label: 'Zamknięty',   fg: 'var(--text-muted)',     bg: 'var(--surface-fill)' },
};

/**
 * Napis odznaki etapu w WIERSZU listy. Dla DO_POWROTU: „Gotowy · N nieobecnych" (N = apt_open:
 * lokale z nieobecnością/odmową lokatora, do których trzeba wrócić); bez otwartych lokali
 * (budynek 0-lokalowy z otwartym wynikiem) — „Gotowy · do powrotu". Pozostałe stany: etykieta etapu.
 */
export function stageBadgeLabel(state: LifecycleState, aptOpen: number | null | undefined): string {
  const base = (LIFECYCLE_META[state] || LIFECYCLE_META.NOWY).label;
  if (state !== 'DO_POWROTU') return base;
  const n = Number(aptOpen) || 0;
  if (n <= 0) return `${base} · do powrotu`;
  return `${base} · ${n} ${pluralPl(n, ['nieobecny', 'nieobecne', 'nieobecnych'])}`;
}
